import Space from 'antd/lib/space';
import * as React from 'react';
import {
    AppDistributionService,
    Automation,
    AutomationType,
    CIBuildInfo,
    CIBuildSelectionReason,
    IntegrationCapability,
    IntegrationInstallationId,
    ReleaseCandidateBuildInfo,
    StabilityMonitoringService,
} from '../../types';
import { AppBuildComponent, CIBuildComponent } from '../BuildComponents';
import LinkageIcon from '../icons/LinkageIcon';
import { antGrey, drawerCardBodyStyles, drawerCardHeadStyles, tagStyle, titleStyle } from '../styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import { buildTimeStampPrefixForBuildStatus, showArtifactsModuleEvenIfEmpty } from '../../helpers';
import Tag from 'antd/lib/tag';
import {
    statusColorForAppStoreIsProcessedStatus,
    statusColorForBuildStatus,
    displayCIText,
} from '../../helpers/displayHelpers';
import SmallStatusNode from '../SmallStatusNode';
import Divider from 'antd/lib/divider';
import { dayjs } from '@/extendedDayjs';
import { getLocaleDateTimeFormat } from '../../timeHelpers';
import { LockOutlined, SettingOutlined } from '@ant-design/icons';
import Card from 'antd/lib/card';
import ArtifactsModule from '../workflowModule/ArtifactsModule';
import Collapse from 'antd/lib/collapse';
import { Nullable, ReleaseComponentProps, StepRunComponentProps } from '../../typescriptHelpers';

interface FeatureItemBuildProps extends ReleaseComponentProps {
    version: string;
    latestOrSelectedStagingBuild?: ReleaseCandidateBuildInfo;
    latestWorkingBranchBuild?: CIBuildInfo;
    isInLatestOrSelectedStagingBuild: boolean;
    isInLatestWorkingBranchBuild: boolean;
    stabilityMonitoring?: StabilityMonitoringService;
    services: Map<IntegrationInstallationId, AppDistributionService>;
    appIdentifiers: Map<IntegrationCapability, string>;
    artifactsAutomations: Partial<Record<AutomationType, Automation>>;
    isRunwayOrg: boolean;
    isWorkflowsCIStepEnabled: boolean;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
    use24HourTimeFormat: boolean;
}

const FeatureItemBuild: React.FC<FeatureItemBuildProps> = (props: FeatureItemBuildProps) => {
    let build = null;
    let isRCBuild = false;

    if (props.isInLatestOrSelectedStagingBuild && props.latestOrSelectedStagingBuild) {
        build = props.latestOrSelectedStagingBuild;
        isRCBuild = true;
    } else if (props.isInLatestWorkingBranchBuild && props.latestWorkingBranchBuild) {
        build = props.latestWorkingBranchBuild;
    }

    const buildCardTitle = () => {
        return (
            <Space style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <SettingOutlined style={{ fontSize: 18, color: antGrey.grey7, padding: 4, marginRight: '-2px' }} />
                <Text style={{ color: antGrey.grey8, fontWeight: 'normal' }}>Build Info</Text>
            </Space>
        );
    };

    if (!build) {
        return (
            <Card
                title={buildCardTitle()}
                headStyle={{
                    ...drawerCardHeadStyles,
                }}
                bodyStyle={{
                    ...drawerCardBodyStyles,
                }}
                bordered={false}
            >
                <Text type="secondary">
                    This work does not yet appear in any working branch or{' '}
                    {displayCIText(props.isWorkflowsCIStepEnabled, 'lowercase')} builds.
                </Text>
            </Card>
        );
    }

    const timestampMoment =
        build.ciBuild.buildFinishedAt ? dayjs(build.ciBuild.buildFinishedAt)
        : build.ciBuild.buildStartedAt ? dayjs(build.ciBuild.buildStartedAt)
        : null;
    const timestampPrefix = buildTimeStampPrefixForBuildStatus(build.ciBuild.buildStatus);

    const ciBuildComponent = (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space size={12}>
                <SmallStatusNode color={statusColorForBuildStatus(build.ciBuild.buildStatus)} size={10} />
                <CIBuildComponent
                    integrationId={build.ciBuild.integrationId}
                    buildIdentifier={build.ciBuild.buildIdentifier}
                    asLink={true}
                    imgSize={24}
                    buildUrl={build.ciBuild.buildUrl}
                />
            </Space>
            {timestampMoment && (
                <Text type="secondary">
                    {timestampPrefix}{' '}
                    {timestampMoment.format(getLocaleDateTimeFormat('short', props.use24HourTimeFormat))}
                </Text>
            )}
        </div>
    );

    let associatedAppStoreBuildComponent = null;

    if (build.associatedAppStoreBuild) {
        const timestampMoment = dayjs(build.associatedAppStoreBuild.uploadedAt);
        const timestampPrefix = 'uploaded';

        associatedAppStoreBuildComponent = (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space size={12}>
                    <SmallStatusNode
                        color={statusColorForAppStoreIsProcessedStatus(build.associatedAppStoreBuild.isProcessed)}
                        size={10}
                    />
                    <AppBuildComponent
                        integrationId={build.associatedAppStoreBuild.integrationId}
                        buildIdentifier={build.associatedAppStoreBuild.buildNumber}
                        imgSize={24}
                        isProcessed={true} // force processing text not to show
                    />
                </Space>
                {timestampMoment && (
                    <Text type="secondary">
                        {timestampPrefix}{' '}
                        {timestampMoment.format(getLocaleDateTimeFormat('short', props.use24HourTimeFormat))}
                    </Text>
                )}
            </div>
        );
    }

    const showLocked =
        build.isSelectedBuild &&
        build.selectedReason &&
        build.selectedReason === CIBuildSelectionReason.ManualSelection;

    const showExtraMessage = (isRCBuild && !showLocked) || !isRCBuild;
    const extraMessage =
        isRCBuild ?
            !showLocked ? `This is the latest ${displayCIText(props.isWorkflowsCIStepEnabled, 'lowercase')} build.`
            :   null
        :   `This work is not yet available on a ${displayCIText(props.isWorkflowsCIStepEnabled, 'lowercase')} build.`;

    const showArtifactsModule = showArtifactsModuleEvenIfEmpty(props.artifactsAutomations);

    return (
        <Card
            title={buildCardTitle()}
            headStyle={{
                ...drawerCardHeadStyles,
            }}
            bodyStyle={{
                ...drawerCardBodyStyles,
            }}
            bordered={false}
            extra={
                <Tag
                    color={isRCBuild ? antGrey.grey7 : 'white'}
                    style={{
                        ...tagStyle,
                        fontSize: 10,
                        borderColor: isRCBuild ? antGrey.grey7 : antGrey.grey5,
                        color: isRCBuild ? 'white' : antGrey.grey7,
                    }}
                >
                    {isRCBuild ?
                        `${displayCIText(props.isWorkflowsCIStepEnabled, 'capitalize')} build`
                    :   'Working branch build'}
                </Tag>
            }
        >
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                {showLocked && (
                    <Space>
                        <LockOutlined style={{ color: antGrey.grey8 }} />
                        <Text type="secondary">Locked</Text>
                    </Space>
                )}
                <Space style={{ width: '100%', marginTop: 8 }} direction="vertical" size={4}>
                    {ciBuildComponent}
                    {associatedAppStoreBuildComponent && ciBuildComponent && (
                        <Divider
                            orientation="left"
                            dashed
                            style={{
                                borderTopColor: antGrey.grey5,
                                marginTop: 0,
                                marginBottom: 0,
                                marginLeft: -3,
                            }}
                            orientationMargin={0}
                            className="fr-build-divider"
                        >
                            <LinkageIcon width={16} height={16} style={{ transform: 'rotate(90deg)' }} />
                        </Divider>
                    )}
                    {associatedAppStoreBuildComponent}
                </Space>
                {showExtraMessage && <Text type="secondary">{extraMessage}</Text>}
                {build.ciBuild.artifacts && build.ciBuild.artifacts.length > 0 && (
                    <Collapse ghost style={{ padding: 0 }} className="collapse-no-padding">
                        <Collapse.Panel
                            header={
                                <Text
                                    style={{
                                        ...titleStyle,
                                        color: antGrey.grey7,
                                        fontSize: 12,
                                        lineHeight: '22px',
                                    }}
                                >
                                    Artifacts
                                </Text>
                            }
                            key="artifacts"
                            className="collapse-panel-no-padding"
                        >
                            {showArtifactsModule && (
                                <ArtifactsModule
                                    appId={props.appId}
                                    orgId={props.orgId}
                                    releaseId={props.releaseId}
                                    version={props.version}
                                    artifacts={build.ciBuild.artifacts ?? []}
                                    lightStyle={true}
                                    stabilityMonitoring={props.stabilityMonitoring}
                                    appIdentifiers={props.appIdentifiers}
                                    buildStatus={build.buildStatus}
                                    canRetry={true}
                                    services={props.services}
                                    showHeader={true}
                                    isPublic={false}
                                    stepRunComponentProps={props.stepRunComponentProps}
                                />
                            )}
                        </Collapse.Panel>
                    </Collapse>
                )}
            </Space>
        </Card>
    );
};

export default FeatureItemBuild;
